import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const { i18n, t } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setShowLanguageMenu(false);
  };

  const getCurrentLanguageLabel = () => {
    switch (i18n.language) {
      case 'nl':
        return 'NL';
      case 'en':
        return 'EN';
      case 'fr':
        return 'FR';
      default:
        return 'EN';
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <div className="flex items-center">
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-teal-700 transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
            
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className={`${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white hover:text-slate-200'} font-medium text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-1`}
              >
                <i className="ri-global-line text-lg"></i>
                <span>{getCurrentLanguageLabel()}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`}></i>
              </button>
              
              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg py-2 border border-slate-200">
                  <button
                    onClick={() => changeLanguage('nl')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'nl' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    Nederlands
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'en' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('fr')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'fr' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    Français
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center space-x-4">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-teal-700 transition-all shadow-sm cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
            
            {/* Mobile Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className={`${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white hover:text-slate-200'} font-medium text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-1`}
              >
                <i className="ri-global-line text-lg"></i>
                <span>{getCurrentLanguageLabel()}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${showLanguageMenu ? 'rotate-180' : ''}`}></i>
              </button>
              
              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg py-2 border border-slate-200">
                  <button
                    onClick={() => changeLanguage('nl')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'nl' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    Nederlands
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'en' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('fr')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors cursor-pointer ${i18n.language === 'fr' ? 'text-teal-600 font-medium' : 'text-slate-700'}`}
                  >
                    Français
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
